import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, RefreshCw, ExternalLink, Bell, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import AdminGate from "@/components/AdminGate";

function ConsultAdminPageBody() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem("consult-admin-key") || "");
  const [pass, setPass] = useState(adminKey);
  const [rows, setRows] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("bookings"); // bookings | notifications

  const load = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const [bs, ns] = await Promise.all([
        api.consultAdminList(adminKey),
        api.consultAdminNotifications(adminKey),
      ]);
      setRows(bs);
      setNotifs(ns);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
      if (e?.response?.status === 401) {
        setAdminKey(""); sessionStorage.removeItem("consult-admin-key");
      }
    }
    setLoading(false);
  }, [adminKey]);

  const testNotify = async () => {
    try {
      const r = await api.consultAdminTestNotify(adminKey);
      const wa = r.result?.whatsapp?.status || "?";
      const link = r.result?.whatsapp?.wa_link;
      toast[r.auto_enabled ? "success" : "info"](
        `WhatsApp ${r.auto_enabled ? "auto-sent" : "pending manual — click Open WhatsApp"}: ${wa}`,
      );
      if (link && !r.auto_enabled) {
        window.open(link, "_blank", "noopener,noreferrer");
      }
      await load();
      setTab("notifications");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test-notify failed");
    }
  };

  useEffect(() => { load(); }, [load]);

  const submit = (e) => {
    e.preventDefault();
    sessionStorage.setItem("consult-admin-key", pass);
    setAdminKey(pass);
  };

  return (
    <div className="min-h-screen bg-[#0F1115] text-[#E6E1D6]">
      <div className="grain-overlay pointer-events-none fixed inset-0 z-0" />
      <header className="relative z-10 border-b border-[#2A2D35] bg-[#0F1115]/90 backdrop-blur-md sticky top-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#A19E95] hover:text-[#D4AF37]">
            <ChevronLeft size={18} />
            <div>
              <div className="text-[9px] uppercase tracking-[0.4em] text-[#6E6B64] font-mono">Back</div>
              <div className="font-heading text-base">Insight Engine</div>
            </div>
          </Link>
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-[0.4em] text-[#D4AF37] font-mono">Admin · Consultations</div>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {!adminKey ? (
          <form onSubmit={submit} className="max-w-md border border-[#2A2D35] bg-[#181A1F]/60 p-6 space-y-4">
            <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">Enter admin key</div>
            <input type="password" value={pass} onChange={e => setPass(e.target.value)}
                   data-testid="admin-key-input"
                   className="w-full bg-[#0F1115] border border-[#2A2D35] text-[#E6E1D6] px-3 py-2 text-sm font-mono focus:border-[#D4AF37] outline-none"
                   placeholder="admin key" autoFocus />
            <Button type="submit" data-testid="admin-key-submit"
                    className="w-full rounded-sm bg-[#D4AF37] text-[#0F1115] hover:bg-[#E5C158]">Unlock</Button>
          </form>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-mono">Consultation Admin</div>
                <div className="font-heading text-3xl mt-1">
                  {tab === "bookings" ? "All Slots" : "Notification Log"}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button onClick={testNotify} data-testid="admin-test-notify"
                        className="rounded-sm border border-[#A8896C] bg-transparent text-[#A8896C] hover:bg-[#A8896C]/10">
                  <Bell size={14} className="mr-2" /> Test Notify
                </Button>
                <Button onClick={load} data-testid="admin-refresh"
                        className="rounded-sm border border-[#D4AF37] bg-transparent text-[#D4AF37] hover:bg-[#D4AF37]/10">
                  <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
                </Button>
              </div>
            </div>

            <div className="mb-4 border-b border-[#2A2D35] flex gap-1">
              {[
                ["bookings", "Bookings", MessageSquare, null],
                ["notifications", "Notifications", Bell,
                  notifs.filter(n => n.status === "pending_manual").length],
              ].map(([k, label, Icon, pending]) => (
                <button key={k} onClick={() => setTab(k)}
                        data-testid={`admin-tab-${k}`}
                        className={`flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-widest font-mono border-b-2 -mb-px transition
                          ${tab === k ? "border-[#D4AF37] text-[#D4AF37]"
                                       : "border-transparent text-[#6E6B64] hover:text-[#A19E95]"}`}>
                  <Icon size={12} /> {label}
                  {pending > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1
                                    bg-[#25D366] text-[#0F1115] text-[10px] rounded-full animate-pulse">
                      {pending}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {tab === "bookings" ? (
            <div className="border border-[#2A2D35] overflow-x-auto">
              <table className="w-full text-sm font-mono">
                <thead className="bg-[#181A1F] text-[#A19E95] text-[10px] uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-3 py-3">Slot</th>
                    <th className="text-left px-3 py-3">Name / Phone</th>
                    <th className="text-left px-3 py-3">Min</th>
                    <th className="text-left px-3 py-3">Mode</th>
                    <th className="text-left px-3 py-3">₹</th>
                    <th className="text-left px-3 py-3">Status</th>
                    <th className="text-left px-3 py-3">Reminder</th>
                    <th className="text-left px-3 py-3">Room</th>
                  </tr>
                </thead>
                <tbody data-testid="admin-rows">
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="px-3 py-6 text-[#6E6B64] text-center">No bookings yet.</td></tr>
                  )}
                  {rows.map(r => (
                    <tr key={r.booking_id} className="border-t border-[#2A2D35]">
                      <td className="px-3 py-2">{new Date(r.slot_start).toLocaleString()}</td>
                      <td className="px-3 py-2">
                        <div className="text-[#E6E1D6]">{r.name}</div>
                        <div className="text-[10px] text-[#A8896C]">{r.phone}</div>
                      </td>
                      <td className="px-3 py-2">{r.minutes}</td>
                      <td className="px-3 py-2 capitalize">{r.mode}</td>
                      <td className="px-3 py-2">{r.amount_inr}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border
                          ${r.status === "paid" ? "border-[#2ecc71] text-[#2ecc71]"
                                                 : "border-[#A19E95] text-[#A19E95]"}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[10px] text-[#A8896C]">
                        {r.reminder_sent_at ? new Date(r.reminder_sent_at).toLocaleTimeString() : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Link to={`/consult/${r.booking_id}`}
                              className="inline-flex items-center gap-1 text-[#D4AF37] hover:underline">
                          Open <ExternalLink size={12} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            ) : (
            <div className="border border-[#2A2D35] overflow-x-auto">
              <table className="w-full text-sm font-mono">
                <thead className="bg-[#181A1F] text-[#A19E95] text-[10px] uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-3 py-3">When</th>
                    <th className="text-left px-3 py-3">Purpose</th>
                    <th className="text-left px-3 py-3">To</th>
                    <th className="text-left px-3 py-3">Status</th>
                    <th className="text-left px-3 py-3">Action</th>
                  </tr>
                </thead>
                <tbody data-testid="admin-notif-rows">
                  {notifs.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-6 text-[#6E6B64] text-center">
                      No notifications yet. Click <span className="text-[#A8896C]">Test Notify</span> to trigger one.
                    </td></tr>
                  )}
                  {notifs.map(n => (
                    <tr key={n.notification_id} className="border-t border-[#2A2D35]">
                      <td className="px-3 py-2">{new Date(n.ts).toLocaleString()}</td>
                      <td className="px-3 py-2 capitalize">{n.purpose.replace("_", " ")}</td>
                      <td className="px-3 py-2">{n.to}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border
                          ${n.status === "sent" ? "border-[#2ecc71] text-[#2ecc71]"
                            : n.status === "failed" ? "border-red-500 text-red-400"
                            : "border-[#25D366] text-[#25D366]"}`}>
                          {n.status === "pending_manual" ? "pending" : n.status}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {n.wa_link ? (
                          <a href={n.wa_link} target="_blank" rel="noopener noreferrer"
                             data-testid={`notif-open-wa-${n.notification_id}`}
                             className="inline-flex items-center gap-1.5 border border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 px-2 py-1 text-[10px] uppercase tracking-widest">
                            <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.695.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                            Open WhatsApp
                          </a>
                        ) : (
                          <span className="text-[10px] text-[#6E6B64]">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default function ConsultAdminPage() {
  return <AdminGate subtitle="Consult Booking Admin"><ConsultAdminPageBody /></AdminGate>;
}
